import { z } from 'zod';
import { objectId } from './schemas.js';
/**
 * The things the company owns and hands out: the laptop somebody is typing on,
 * and the box of them in the cupboard.
 *
 * Two shapes, because two genuinely different questions are being asked.
 * "Who has serial number C02X?" is about one identified object with a history,
 * and that is an `Asset`. "Do we have any USB-C hubs left?" is about a count,
 * and tracking those individually would mean fifty near-identical rows nobody
 * maintains — that is a `StockItem`.
 *
 * Assignment is the journey worth getting right. An asset handed over without
 * a record is how companies discover at an exit interview that nobody knows
 * what the leaver has.
 */
export const ASSET_CATEGORIES = [
    'laptop',
    'desktop',
    'monitor',
    'phone',
    'tablet',
    'peripheral',
    'furniture',
    'vehicle',
    'software',
    'other',
];
/**
 * Where a thing is.
 *
 * `in-stock` and `assigned` are the working pair. `repair` is separate from
 * both because a laptop at the repair shop is neither available to give out
 * nor with the person it belongs to, and reporting it as either would send
 * somebody looking in the wrong place.
 */
export const ASSET_STATUSES = ['in-stock', 'assigned', 'repair', 'retired', 'lost'];
/** Statuses a thing can still be given to somebody from. */
export const isAssignable = (status) => status === 'in-stock' || status === 'repair';
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-16');
export const createAssetSchema = z.object({
    tag: z.string().trim().min(1).max(40),
    name: z.string().trim().min(2).max(120),
    category: z.enum(ASSET_CATEGORIES).default('other'),
    serial: z.string().trim().max(80).optional(),
    vendorId: objectId.nullable().optional(),
    purchaseDate: dateOnly.optional(),
    purchaseAmount: z.number().int().nonnegative().max(1_000_000_000_000).optional(),
    warrantyEnd: dateOnly.optional(),
    departmentId: objectId.nullable().optional(),
    location: z.string().trim().max(120).optional(),
    note: z.string().trim().max(2000).optional(),
});
/**
 * Status is not patchable here. Every status this module has is the outcome of
 * an action with its own endpoint — assign, return, retire — and each of those
 * writes a history row. A free `status` field would let the register say
 * "assigned" with nobody's name against it.
 *
 * The dates and the cost take `null` on update where they take nothing on
 * create: a warranty date typed against the wrong machine has to be removable,
 * and a date pattern cannot express "no date" as an empty string.
 */
export const updateAssetSchema = createAssetSchema.partial().extend({
    purchaseDate: dateOnly.nullable().optional(),
    warrantyEnd: dateOnly.nullable().optional(),
    purchaseAmount: z.number().int().nonnegative().max(1_000_000_000_000).nullable().optional(),
});
export const assetQuerySchema = z.object({
    status: z.enum(ASSET_STATUSES).optional(),
    category: z.enum(ASSET_CATEGORIES).optional(),
    assignedTo: objectId.optional(),
    q: z.string().trim().max(80).optional(),
});
export const assignAssetSchema = z.object({
    userId: objectId,
    note: z.string().trim().max(500).optional(),
});
export const CONDITIONS = ['good', 'worn', 'damaged', 'lost'];
export const returnAssetSchema = z.object({
    condition: z.enum(CONDITIONS).default('good'),
    note: z.string().trim().max(500).optional(),
});
export const retireAssetSchema = z.object({
    status: z.enum(['retired', 'lost', 'repair', 'in-stock']),
    note: z.string().trim().max(500).optional(),
});
/**
 * Registering what a received purchase order actually brought.
 *
 * The order already says what was bought, from whom, on what day and for how
 * much — so the only thing a person should have to type is the sticker they
 * are putting on the case. Anything else is a second, divergent record of a
 * purchase that has already been agreed and paid for.
 *
 * One entry per physical thing: an order line for three monitors becomes three
 * tags, because three monitors are three things that end up on three desks.
 */
export const registerFromOrderSchema = z.object({
    orderId: objectId,
    items: z
        .array(z.object({
        /** Which line of the order this thing came from. */
        lineIndex: z.number().int().min(0).max(49),
        tag: z.string().trim().min(1).max(40),
        category: z.enum(ASSET_CATEGORIES).optional(),
        serial: z.string().trim().max(80).optional(),
    }))
        .min(1)
        .max(50),
});
/* -------------------------------- stock --------------------------------- */
export const STOCK_UNITS = ['each', 'box', 'pack', 'litre', 'kilogram', 'metre'];
export const createStockItemSchema = z.object({
    name: z.string().trim().min(2).max(120),
    sku: z.string().trim().max(60).optional(),
    unit: z.enum(STOCK_UNITS).default('each'),
    /** The opening count. Every change after this one is a movement. */
    quantity: z.number().int().min(0).max(1_000_000).default(0),
    reorderLevel: z.number().int().min(0).max(1_000_000).default(0),
    location: z.string().trim().max(120).optional(),
    vendorId: objectId.nullable().optional(),
    unitAmount: z.number().int().nonnegative().max(1_000_000_000_000).optional(),
    note: z.string().trim().max(1000).optional(),
});
/** Same reasoning as the register: what one costs has to be removable. */
export const updateStockItemSchema = createStockItemSchema
    .omit({ quantity: true })
    .partial()
    .extend({
    unitAmount: z.number().int().nonnegative().max(1_000_000_000_000).nullable().optional(),
});
export const STOCK_REASONS = ['received', 'issued', 'returned', 'written-off', 'counted'];
/**
 * A movement says which way it went in `reason` and how much in `quantity` —
 * a signed delta from the client is one typo away from a stock take that adds
 * what it meant to remove.
 *
 * `counted` is the exception and the reason this is not a plain add: after a
 * physical count the number in the cupboard is known absolutely, and the
 * movement that records it is whatever it takes to get there. Its `quantity`
 * is the count, not a change, and may legitimately be zero.
 */
export const createMovementSchema = z.object({
    reason: z.enum(STOCK_REASONS),
    quantity: z.number().int().min(0).max(1_000_000),
    issuedTo: objectId.nullable().optional(),
    note: z.string().trim().max(500).optional(),
});
/** The change one movement makes, given what is on the shelf right now. */
export function movementDelta(reason, quantity, balance) {
    if (reason === 'counted')
        return quantity - balance;
    return reason === 'received' || reason === 'returned' ? quantity : -quantity;
}
